"use client";

import { clsx } from "@trydig/ui";
import _zip from "lodash/zip";
import { useMemo, useState } from "react";
import { NeedleCartItem } from "~/components/ui/NeedleCartItem";
import type {
	TCartItemWithQuantity,
	TNeedles,
} from "~/components/ui/NeedleCartItem/NeedleCartItem";
import { useQuantityInput } from "~/components/ui/VariantPicker/hooks/useQuantityInput";
import { MicroCarts } from "~/features/cart/MicroCart/MicroCartContext";
import { useMicroCart } from "~/features/cart/MicroCart/useMicroCart";
import styles from "./NeedlePicker.module.css";

const useCartItem = (needles: TNeedles) => {
	const length = needles.items.length;
	const defaultQuantity = 0;
	const defaultValues = Array(length).fill(defaultQuantity);
	const quantityInput = useQuantityInput(length, defaultValues);

	const resetAll = () => {
		quantityInput.forEach((input) => {
			input.update(defaultQuantity);
		});
	};

	const items = useMemo(
		() =>
			_zip(needles.items, quantityInput).map(([item, quantityInput]) => {
				return {
					...item,
					quantityInput,
				};
			}) as TCartItemWithQuantity[],
		[needles.items, quantityInput],
	);

	useMicroCart(
		MicroCarts.Needles,
		items,
		{
			id: "size.id",
			quantity: "quantityInput.value",
		},
		resetAll,
	);

	return {
		...needles,
		items,
	};
};

const MAX_ITEMS_TO_SHOW = 2;

export const ClientNeedlePicker = ({ needles }: { needles: TNeedles }) => {
	const [showAll, setShowAll] = useState(false);
	const { items, recommendationText } = useCartItem(needles);

	const itemsToShow = showAll ? items : items.slice(0, MAX_ITEMS_TO_SHOW);

	return (
		<section className={clsx("pdp-card variant-picker")}>
			<p className="pdp-sub-container-title">Strikkepinner</p>
			<p className="pdp-sub-container-label">{recommendationText} </p>
			{itemsToShow?.map((item, index) => {
				return (
					<NeedleCartItem item={item} key={`${item.size?.data.name}-${item?.needle}-${index}`} />
				);
			})}
			{items?.length > MAX_ITEMS_TO_SHOW && (
				<button
					onClick={() => setShowAll(!showAll)}
					className={clsx("transparent", styles.showMoreBtn)}
				>
					{showAll ? "Vis færre" : "Vis flere"}
				</button>
			)}
		</section>
	);
};
